import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Vendor {
  id: number
  display_name: string
}

interface Contact {
  id: number
  display_name: string
  prefix: string | null
  first_name: string | null
  last_name: string | null
  title: string | null
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  phone: string | null
  cell_phone: string | null
  email: string | null
  notes: string | null
  created_by: string | null
  created_at: string
}

interface Props {
  vendor: Vendor
  contact: Contact
  can_edit: boolean
}

function formatPhone(phone: string | null): string {
  if (!phone) return ""
  const digits = phone.replace(/\D/g, "")
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}

export default function Show({ vendor, contact, can_edit }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Subcontractors", href: "/company/vendors" },
    { title: vendor.display_name, href: `/company/vendors/${vendor.id}` },
    { title: "Contacts", href: `/company/vendors/${vendor.id}/contacts` },
    {
      title: contact.display_name,
      href: `/company/vendors/${vendor.id}/contacts/${contact.id}`,
    },
  ]

  const handleDelete = () => {
    if (confirm("Are you sure you want to remove this contact?")) {
      router.delete(`/company/vendors/${vendor.id}/contacts/${contact.id}`)
    }
  }

  const hasAddress = contact.address ?? contact.city ?? contact.state
  const hasPhone = contact.phone ?? contact.cell_phone
  const hasContactInfo = hasPhone ?? contact.email

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${contact.display_name} - Contact`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{contact.display_name}</h1>
            {contact.title && (
              <div className="text-hpi-med-gray text-sm">{contact.title}</div>
            )}
          </div>
          {can_edit && (
            <div className="space-x-2">
              <Link
                href={`/company/vendors/${vendor.id}/contacts/${contact.id}/edit`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
              >
                Edit
              </Link>
              <button
                onClick={handleDelete}
                className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
              >
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          {hasAddress && (
            <div>
              {contact.address && <div>{contact.address}</div>}
              {contact.address2 && <div>{contact.address2}</div>}
              {contact.city && (
                <div>
                  {[contact.city, contact.state].filter(Boolean).join(", ")}{" "}
                  {contact.zip}
                </div>
              )}
            </div>
          )}

          {hasContactInfo && (
            <div>
              {contact.phone && (
                <div>
                  <span className="font-semibold">Office:</span>{" "}
                  {formatPhone(contact.phone)}
                </div>
              )}
              {contact.cell_phone && (
                <div>
                  <span className="font-semibold">Cell:</span>{" "}
                  {formatPhone(contact.cell_phone)}
                </div>
              )}
              {contact.email && (
                <div>
                  <span className="font-semibold">Email:</span> {contact.email}
                </div>
              )}
            </div>
          )}
        </div>

        {contact.notes && (
          <div className="mb-6">
            <span className="font-semibold">Notes:</span>
            <p className="mt-1 whitespace-pre-wrap">{contact.notes}</p>
          </div>
        )}

        <div className="text-hpi-med-gray text-sm">
          Added {formatDate(contact.created_at)}
          {contact.created_by && ` by ${contact.created_by}`}
        </div>

        <div className="mt-4">
          <Link
            href={`/company/vendors/${vendor.id}/contacts`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            &larr; Back to Contacts
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
